import React, { useState, useEffect } from 'react';
import { useLocation as useAppLocation } from '../../contexts/LocationContext';
import { bindDownloadHandler } from '../../utils/downloadUtils';
import { ConsumptionBasedGHGInventoryWithMetrics } from '../charts/ConsumptionBasedGHGInventoryWithMetrics';
import { SectorBasedGHGInventoryWithMetrics } from '../charts/SectorBasedGHGInventoryWithMetrics';
import { InventoryComparisonChart } from '../charts/InventoryComparisonChart';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import { PageHeader } from '../layout/PageHeader';
import { fetchInventoryComparisonData } from '../../services/inventoryComparisonService';
import {
  GHG_LENS_COMPARISON_KEY_INSIGHTS,
  GHG_LENS_COMPARISON_OVERVIEW,
  GHG_LENS_KPI_LABELS,
} from '../../domains/narrative/ghgAccountingLensesCopy';
import GenericMapTiered from '../charts/GenericMapTiered';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import SectionWrapper, { useSectionReveal } from './SectionWrapper';
import { GhgInventoryReportPreviewLink } from '../ghgInventoryReport/GhgInventoryReportPreviewLink';

import sectorBasedGhgBg from '../../assets/ghg/sector-based-ghg-bg.jpg';
import consumptionBasedGhgBg from '../../assets/ghg/consumption-based-ghg-bg.jpg';
import inventoryComparisonBg from '../../assets/ghg/inventory-comparison-bg.jpg';
import carbonFootprintMapBg from '../../assets/ghg/carbon-footprint-map-bg.jpg';

const GHG_SECTION_BG: Record<string, string> = {
  sector_based_ghg: sectorBasedGhgBg,
  consumption_based_ghg: consumptionBasedGhgBg,
  inventory_comparison: inventoryComparisonBg,
  carbon_footprint_map: carbonFootprintMapBg,
};

const GHG_SECTION_IDS = Object.keys(GHG_SECTION_BG);

interface GHGInventoryProps {
  location?: string;
}

export const GHGInventory: React.FC<GHGInventoryProps> = ({ location: propLocation }) => {
  const { selectedLocation: ctxLocation, selectedGeofips, coreDataLoading } = useAppLocation();
  const [selectedLocation] = useState<string>('');
  const [inventoryComparisonData, setInventoryComparisonData] = useState<any>(null);
  const [inventoryComparisonLoading, setInventoryComparisonLoading] = useState(true);
  const [inventoryComparisonActiveView, setInventoryComparisonActiveView] = useState<string>('overview');
  const [mapActiveView, setMapActiveView] = useState<string>('overview');
  const [comparisonDownloadPNG, setComparisonDownloadPNG] = useState<(() => void) | undefined>();
  const [comparisonDownloadCSV, setComparisonDownloadCSV] = useState<(() => void) | undefined>();
  const [mapDownloadPNG, setMapDownloadPNG] = useState<(() => void) | undefined>();
  const [mapDownloadCSV, setMapDownloadCSV] = useState<(() => void) | undefined>();
  const [carbonFootprintMetrics, setCarbonFootprintMetrics] = useState<{
    currentValue: number;
    californiaAverage: number | null;
    year: number;
    variable: string;
  } | null>(null);

  const currentLocation = propLocation || ctxLocation || selectedLocation;
  const {
    isSectionEnabled,
    getSectionOrder
  } = useAdminPageSettings('ghg_inventory', currentLocation);

  const getSectionStyle = (sectionId: string) => ({
    order: getSectionOrder(sectionId),
    display: isSectionEnabled(sectionId) ? 'block' : 'none'
  });

  const { visible: sectionVisible, refs: sectionRefs } = useSectionReveal(
    GHG_SECTION_IDS,
    !coreDataLoading && !!currentLocation
  );

  // Load inventory comparison data
  useEffect(() => {
    const loadInventoryComparisonData = async () => {
      if (!currentLocation) return;
      
      try {
        setInventoryComparisonLoading(true);
        const result = await fetchInventoryComparisonData(currentLocation);
        setInventoryComparisonData(result);
      } catch (error) {
        console.error('Error loading inventory comparison data:', error);
      } finally {
        setInventoryComparisonLoading(false);
      }
    };

    loadInventoryComparisonData();
  }, [currentLocation]);

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="GHG Inventory" 
          subtitle="Please select a location to view greenhouse gas inventory data."
        />
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div>
        <PageHeader 
          title={"Loading core data..."}
          subtitle={"Preloading VMT, electricity, and natural gas data for this location."}
        />
      </div>
    );
  }

  return (
    <div data-outreach-snapshot="ghg-inventory">
      <PageHeader 
        title={`GHG Inventory - ${currentLocation}`}
        subtitle="Comprehensive greenhouse gas emissions tracking and analysis for the selected location."
        variant="ghg"
      />
      <GhgInventoryReportPreviewLink geofips={selectedGeofips} />

      {/* Sector-Based GHG Inventory Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <SectionWrapper sectionId="sector_based_ghg" bgMap={GHG_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <SectorBasedGHGInventoryWithMetrics location={currentLocation} />
      </SectionWrapper>

      {/* Consumption-Based GHG Inventory Section */}
      <SectionWrapper sectionId="consumption_based_ghg" bgMap={GHG_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <ConsumptionBasedGHGInventoryWithMetrics location={currentLocation} />
      </SectionWrapper>

      {/* Inventory Comparison Section */}
      <SectionWrapper sectionId="inventory_comparison" bgMap={GHG_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Inventory Comparison"
        description="Comparison between sector-based and consumption-based GHG inventory approaches."
        chartType="line"
        keyMetrics={
          inventoryComparisonLoading ? (
            <KeyMetrics>
              <KeyMetric 
                value="Loading..." 
                label={GHG_LENS_KPI_LABELS.sector}
                color="#6b7280"
              />
              <KeyMetric 
                value="Loading..." 
                label={GHG_LENS_KPI_LABELS.consumption}
                color="#6b7280"
              />
              <KeyMetric 
                value="Loading..." 
                label={GHG_LENS_KPI_LABELS.note}
                color="#6b7280"
              />
            </KeyMetrics>
          ) : inventoryComparisonData ? (
            <KeyMetrics>
              <KeyMetric 
                value={`${inventoryComparisonData.sectorBasedTotal.toLocaleString()}`}
                label={GHG_LENS_KPI_LABELS.sector}
                color="#3b82f6"
              />
              <KeyMetric 
                value={
                  inventoryComparisonData.consumptionPerHouseholdMtCo2e != null
                    ? inventoryComparisonData.consumptionPerHouseholdMtCo2e.toFixed(1)
                    : '—'
                }
                label={GHG_LENS_KPI_LABELS.consumption}
                color="#10b981"
              />
              <KeyMetric 
                value={String(inventoryComparisonData.latestYear ?? '—')}
                label={GHG_LENS_KPI_LABELS.note}
                color="#6b7280"
              />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric 
                value="No data" 
                label={GHG_LENS_KPI_LABELS.sector}
                color="#6b7280"
              />
              <KeyMetric 
                value="No data" 
                label={GHG_LENS_KPI_LABELS.consumption}
                color="#6b7280"
              />
              <KeyMetric 
                value="No data" 
                label={GHG_LENS_KPI_LABELS.note}
                color="#6b7280"
              />
            </KeyMetrics>
          )
        }
        chartContent={
          <InventoryComparisonChart
            location={currentLocation}
            onDownloadPNGReady={bindDownloadHandler(setComparisonDownloadPNG)}
            onDownloadCSVReady={bindDownloadHandler(setComparisonDownloadCSV)}
          />
        }
        overviewContent={
          <OverviewContent
            title="Inventory Comparison Overview"
            description={GHG_LENS_COMPARISON_OVERVIEW}
          >
            <p>{GHG_LENS_COMPARISON_KEY_INSIGHTS}</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Comparing inventory approaches helps communities understand the full scope of their climate impact and develop more effective strategies."
          >
            <div className="methodology-details-simple">
              <h5>Comprehensive Planning</h5>
              <p>Understanding both approaches enables communities to develop strategies that address emissions from multiple angles.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Different inventory approaches may require different policy interventions and behavioral change strategies.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Stakeholder Engagement</h5>
              <p>Both approaches engage different stakeholders - sector-based focuses on businesses and infrastructure, consumption-based on residents and lifestyle choices.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Different inventory approaches may reveal different equity implications and require different approaches to ensure equitable climate action."
          >
            <div className="methodology-details-simple">
              <h5>Consumption Patterns</h5>
              <p>Consumption-based inventories may reveal disparities in emissions based on income, access to alternatives, and lifestyle choices.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Geographic Boundaries</h5>
              <p>Sector-based inventories focus on local emissions, while consumption-based inventories consider global supply chains and their equity implications.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Impact</h5>
              <p>Different approaches may affect different communities differently, requiring careful consideration of equity in policy design.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="The comparison uses the same base year and geographic boundaries to ensure meaningful comparison between inventory approaches."
            dataSources={[
              'Sector-based inventory data from local emissions tracking',
              'Consumption-based inventory data from CBEI model',
              'Population and household data for scaling',
              'Economic and consumption pattern data'
            ]}
            methodology={[
              'Community (sector-based) inventory: in-boundary activity accounting from ClimatePlans GHG inventory pipeline.',
              'Household consumption inventory: CBEI-modeled consumption footprints (cbei_cities / cbei_counties) — different geographic and sector allocation rules.',
              'The chart shows parallel time series only; do not compute difference, gap, or ratio between the two lenses without a dedicated reconciliation study.',
              'Limitations: Different methodologies answer different policy questions and must not be treated as competing estimates of one total.'
            ]}
          />
        }
        activeView={inventoryComparisonActiveView}
        onViewChange={setInventoryComparisonActiveView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={comparisonDownloadPNG}
        onDownloadCSV={comparisonDownloadCSV}
      />
      </SectionWrapper>

      {/* Average Household Carbon Footprint Map */}
      <SectionWrapper sectionId="carbon_footprint_map" bgMap={GHG_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Average Household Carbon Footprint Map"
        description="Interactive map showing household carbon footprints by census tract for the selected location."
        chartType="map"
        layout="vertical"
        keyMetrics={
          carbonFootprintMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${carbonFootprintMetrics.currentValue.toFixed(1)} MT CO₂e/HH`}
                label="Average Household Carbon Footprint"
              />
              <KeyMetric
                value={
                  carbonFootprintMetrics.californiaAverage != null
                    ? `${carbonFootprintMetrics.californiaAverage.toFixed(1)} MT CO₂e/HH`
                    : '—'
                }
                label="CA Average"
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <GenericMapTiered
            location={currentLocation}
            hideVariableSelector={true}
            lockedVariable="carbon_footprint"
            enableScopeSelector={true}
            onDownloadPNGReady={bindDownloadHandler(setMapDownloadPNG)}
            onDownloadCSVReady={bindDownloadHandler(setMapDownloadCSV)}
            onDataLoaded={(metrics) => {
              setCarbonFootprintMetrics(metrics);
            }}
          />
        }
        overviewContent={
          <OverviewContent
            title="Carbon Footprint Map Overview"
            description="This interactive map displays average household carbon footprints by census tract, providing detailed insights into consumption-based emissions patterns across the community."
          >
            <p>
              The map shows carbon footprints calculated using the Consumption-Based Emissions Inventory (CBEI) methodology, 
              which accounts for emissions from transportation, housing, food, goods, and services consumption patterns. 
              Each census tract is color-coded based on the average household carbon footprint, allowing for detailed 
              geographic analysis of emissions patterns within the community.
            </p>
            <p>
              Use the <strong>Data Scope</strong> control above the map to view county tracts, a full air-quality
              region, or all of California. County scope is fastest; statewide loads the complete tract layer.
            </p>
            <p>
              Click on any tract to view detailed breakdowns of emissions by category, including transportation, 
              housing, food, goods, and services. This granular view helps identify areas with higher or lower 
              carbon footprints and can inform targeted climate action strategies.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Carbon footprint mapping helps communities identify priority areas for emissions reduction and develop targeted interventions."
          >
            <div className="methodology-details-simple">
              <h5>Targeted Interventions</h5>
              <p>High-footprint areas can be prioritized for specific programs like energy efficiency, transportation alternatives, or sustainable consumption initiatives.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Equity Considerations</h5>
              <p>Mapping reveals disparities in carbon footprints that may correlate with income, access to alternatives, or other demographic factors.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Development</h5>
              <p>Geographic patterns inform zoning policies, infrastructure investments, and community-based climate programs.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Carbon footprint mapping reveals important equity implications for climate action planning."
          >
            <div className="methodology-details-simple">
              <h5>Access to Alternatives</h5>
              <p>Areas with limited access to public transit, energy-efficient housing, or sustainable food options may show higher carbon footprints.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Economic Factors</h5>
              <p>Income levels often correlate with consumption patterns and access to low-carbon alternatives, affecting household carbon footprints.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Community Resources</h5>
              <p>Investment in community resources, infrastructure, and services can help reduce carbon footprints while improving quality of life.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Carbon footprint data is calculated using the Consumption-Based Emissions Inventory (CBEI) methodology developed by EcoDataLab."
            dataSources={[
              'EcoDataLab Consumption-Based Emissions Inventory (CBEI)',
              'Census tract-level demographic and economic data',
              'Household consumption pattern surveys',
              'Regional economic input-output models',
              'Transportation and energy use data'
            ]}
            methodology={[
              'Carbon footprints calculated using consumption-based accounting methodology.',
              'Emissions allocated to households based on consumption patterns and lifestyle choices.',
              'Data includes transportation, housing, food, goods, and services consumption.',
              'Values converted from grams to metric tons CO₂ equivalent and rounded to one decimal place.',
              'Map displays average household carbon footprint by census tract for 2023.',
              'Five-class orange scale (darker = higher footprint). Breaks prefer California census tracts when available.',
              'Limitations: Data represents 2023 estimates and may not reflect recent changes in consumption patterns.'
            ]}
          />
        }
        activeView={mapActiveView}
        onViewChange={setMapActiveView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        onDownloadPNG={mapDownloadPNG}
        onDownloadCSV={mapDownloadCSV}
      />
      </SectionWrapper>
      </div>
    </div>
  );
};
